import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Copy as ContentCopyIcon } from 'lucide-react';
import { createUploadLink } from '../../utils/secureUploadApi';

const STEPS = ['Provider', 'Link options', 'Done'];

const EXPIRES_OPTIONS = [1, 7, 14, 30, 90];

const truncateToken = (token) => token ? `${token.slice(0, 12)}…` : '';

const GenerateLinkPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [providerName, setProviderName] = useState('');
  const [formData, setFormData] = useState({
    expires_in_days: 14,
    is_one_time: false,
    max_files: 200,
    max_file_size_mb: 25,
    require_uploader_identity: true,
  });
  const [generatedLink, setGeneratedLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await createUploadLink({ provider_name: providerName, ...formData });
      setGeneratedLink(response.data);
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLink.upload_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Generate Upload Link
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 3 }}>
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Provider name
            </Typography>
            <TextField
              label="Provider name"
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              fullWidth
              required
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              disabled={!providerName.trim()}
              onClick={() => setActiveStep(1)}
            >
              Continue
            </Button>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Link options
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Expires in
            </Typography>
            <Select
              value={formData.expires_in_days}
              onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
              fullWidth
              sx={{ mb: 3 }}
            >
              {EXPIRES_OPTIONS.map((days) => (
                <MenuItem key={days} value={days}>
                  {days} {days === 1 ? 'day' : 'days'}
                </MenuItem>
              ))}
            </Select>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Usage limit
            </Typography>
            <ButtonGroup sx={{ mb: 3 }}>
              <Button
                variant={formData.is_one_time ? 'outlined' : 'contained'}
                onClick={() => setFormData({ ...formData, is_one_time: false })}
              >
                Reusable
              </Button>
              <Button
                variant={formData.is_one_time ? 'contained' : 'outlined'}
                onClick={() => setFormData({ ...formData, is_one_time: true })}
              >
                One-time
              </Button>
            </ButtonGroup>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField
                  label="Max files"
                  type="number"
                  value={formData.max_files}
                  onChange={(e) => setFormData({ ...formData, max_files: parseInt(e.target.value) || 1 })}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Max file size (MB)"
                  type="number"
                  value={formData.max_file_size_mb}
                  onChange={(e) => setFormData({ ...formData, max_file_size_mb: parseInt(e.target.value) || 1 })}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.require_uploader_identity}
                  onChange={(e) => setFormData({ ...formData, require_uploader_identity: e.target.checked })}
                />
              }
              label="Require uploader name + email"
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => setActiveStep(0)}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {loading ? 'Generating…' : 'Generate Link'}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 2 && generatedLink && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Link generated
            </Typography>

            <Box
              sx={{
                fontFamily: 'monospace',
                bgcolor: 'grey.100',
                borderRadius: 1,
                p: 2,
                mb: 2,
                wordBreak: 'break-all',
                fontSize: '0.875rem',
              }}
            >
              {generatedLink.upload_url}
            </Box>

            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              sx={{ mb: 3 }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={1}>
              {[
                ['Provider', generatedLink.provider_name],
                ['Token', truncateToken(generatedLink.token)],
                ['Expires', new Date(generatedLink.expires_at).toLocaleDateString()],
                ['Usage limit', generatedLink.is_one_time ? 'One-time' : 'Reusable'],
                ['Required fields', generatedLink.require_uploader_identity ? 'Name + email' : 'None'],
                ['Created at', new Date(generatedLink.created_at).toLocaleString()],
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2" sx={{ fontFamily: label === 'Token' ? 'monospace' : undefined }}>
                      {value}
                    </Typography>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>

            <Button
              variant="contained"
              sx={{ mt: 3 }}
              onClick={() => navigate('/upload-batches')}
            >
              Done
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default GenerateLinkPage;
